import React, { useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { proposalService, type EventLibraryItem, type EventTag } from '@/services/ProposalService';
import { CalendarDays, Check, Search } from 'lucide-react';

const TAG_OPTIONS: { value: EventTag; label: string; emoji: string }[] = [
  { value: 'off_venue', label: 'Off-Venue', emoji: '🏛️' },
  { value: 'welcome_reception', label: 'Welcome Reception', emoji: '🥂' },
  { value: 'final_dinner', label: 'Final Dinner', emoji: '🍽️' },
  { value: 'nightlife', label: 'Nightlife', emoji: '🌙' },
];

function getTagInfo(tag: EventTag | null) {
  return TAG_OPTIONS.find(t => t.value === tag);
}

interface EventLibraryBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  city: string;
  country: string;
  onEventAdded: () => void;
}

export function EventLibraryBrowser({
  isOpen,
  onClose,
  city,
  country,
  onEventAdded,
}: EventLibraryBrowserProps) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<EventLibraryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setAddedIds(new Set());
      loadLibrary();
    }
  }, [isOpen]);

  async function loadLibrary() {
    setLoading(true);
    try {
      const list = await proposalService.listEventLibrary(false);
      setItems(list);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(item: EventLibraryItem) {
    setAdding(item.id);
    try {
      await proposalService.copyLibraryEventToDestination(item.id, city, country);
      setAddedIds(prev => new Set(prev).add(item.id));
      onEventAdded();
    } catch {
      // handled upstream
    } finally {
      setAdding(null);
    }
  }

  const filtered = items.filter(item => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return item.name.toLowerCase().includes(q) || (item.description?.toLowerCase().includes(q));
  });

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add from Event Library"
      className="max-w-lg"
    >
      <div className="mb-4">
        <Input
          placeholder="Search events..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
          autoFocus
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <CalendarDays className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm">
            {items.length === 0
              ? 'No events in your library yet. Create them in Organization → Event Library.'
              : 'No matching events found.'}
          </p>
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto space-y-2">
          {filtered.map(item => {
            const justAdded = addedIds.has(item.id);
            const isAdding = adding === item.id;
            const tagInfo = getTagInfo(item.tag);

            return (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                {/* Thumbnail */}
                {item.images?.[0]?.url ? (
                  <img
                    src={item.images[0].url}
                    alt=""
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-5 h-5 text-gray-400" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{item.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {tagInfo && (
                      <span className="text-xs text-gray-500">{tagInfo.emoji} {tagInfo.label}</span>
                    )}
                    {item.estimated_price_per_person != null && (
                      <span className="text-xs text-gray-500">${item.estimated_price_per_person}/pp</span>
                    )}
                  </div>
                </div>

                {/* Action */}
                {justAdded ? (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    <Check className="w-3 h-3" /> Added
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdd(item)}
                    disabled={isAdding}
                  >
                    {isAdding ? 'Adding...' : 'Add'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button variant="ghost" onClick={onClose}>Done</Button>
      </div>
    </Dialog>
  );
}
